import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'

import { Chip, InputAdornment, TextField, useTheme } from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import ApartmentIcon from '@material-ui/icons/Apartment'
import FolderOpenIcon from '@material-ui/icons/FolderOpen'
import PeopleIcon from '@material-ui/icons/People'
import SearchIcon from '@material-ui/icons/Search'

import { Container } from '../../components'
import { useDebounce } from '../../hooks/useDebounce'
import {
    useRecordsPeople,
    useRecordsServiceRequestSlice,
    useRecordsUnits,
} from '../../hooks/useRecords'
import { Routes } from '../../navigation/config/routes'
import { typographyStyle } from '../../theme/typographyStyle'
import { AllUnitsPane } from './AllUnitsPane'
import { PersonFile } from './PersonFile'
import {
    buildRecordsSearch,
    mergeRecordsSearch,
    parseRecordsSearch,
    recordsPersonHref,
    recordsUnitPath,
    RecordsPeopleCohort,
    RecordsSearchState,
} from './paths'
import { RecordsIndex } from './RecordsIndex'
import { RecordsPivot } from './types'
import { UnitFile } from './UnitFile'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            backgroundColor: theme.palette.grey[100],
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            height: 'calc(100vh - 104px)',
        },
        header: {
            height: 72,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            padding: theme.spacing(0, 4),
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            flexShrink: 0,
        },
        titleIcon: {
            width: 44,
            height: 44,
            backgroundColor: theme.palette.primary.main,
            borderRadius: theme.spacing(1.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        title: {
            ...(typographyStyle(theme.typography.h6) as Record<
                string,
                unknown
            >),
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.2,
        },
        subtitle: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
        },
        searchField: {
            width: 320,
            '& .MuiOutlinedInput-root': {
                borderRadius: theme.spacing(3),
                backgroundColor: theme.palette.grey[50],
            },
        },
        mainContent: {
            flex: 1,
            display: 'flex',
            minHeight: 0,
            overflow: 'hidden',
        },
        contentArea: {
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
        },
        pane: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(6),
            textAlign: 'center',
        },
        emptyIcon: {
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: theme.palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: theme.spacing(2),
        },
        emptyTitle: {
            ...(typographyStyle(theme.typography.h6) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
        },
        emptyBody: {
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
            maxWidth: 480,
        },
    }),
)

export const RecordsHome = () => {
    const classes = useStyles()
    const theme = useTheme()
    const history = useHistory()
    const location = useLocation()
    const recordsSearch = parseRecordsSearch(location.search)
    const [searchQuery, setSearchQuery] = useState(recordsSearch.q)
    const debouncedSearch = useDebounce(searchQuery.trim(), 300)
    const lastWrittenQ = useRef(recordsSearch.q)

    const unitMatch = useRouteMatch<{ unitId: string }>({
        path: Routes.apartment.records.unit,
        exact: true,
    })
    const personMatch = useRouteMatch<{ userId: string }>({
        path: Routes.apartment.records.person,
        exact: true,
    })
    const leadMatch = useRouteMatch<{ leadId: string }>({
        path: Routes.apartment.records.lead,
        exact: true,
    })
    const allPeopleMatch = useRouteMatch({
        path: Routes.apartment.records.people,
        exact: true,
    })

    const pivot: RecordsPivot =
        personMatch || leadMatch || allPeopleMatch ? 'people' : 'units'
    const selectedUnitId = unitMatch ? Number(unitMatch.params.unitId) : null
    const selectedUserId = personMatch
        ? Number(personMatch.params.userId)
        : null
    const selectedLeadId = leadMatch ? Number(leadMatch.params.leadId) : null
    const safeSelectedUnitId =
        selectedUnitId != null && Number.isFinite(selectedUnitId)
            ? selectedUnitId
            : null
    const safeSelectedUserId =
        selectedUserId != null && Number.isFinite(selectedUserId)
            ? selectedUserId
            : null
    const safeSelectedLeadId =
        selectedLeadId != null && Number.isFinite(selectedLeadId)
            ? selectedLeadId
            : null

    const sliceParams = recordsSearch
    const category = sliceParams.category
    const openOnly = sliceParams.open
    const occupancy = sliceParams.occupancy
    const cohort = sliceParams.cohort || 'current'
    const page = sliceParams.page

    useEffect(() => {
        if (recordsSearch.q === lastWrittenQ.current) return
        lastWrittenQ.current = recordsSearch.q
        setSearchQuery(recordsSearch.q)
    }, [recordsSearch.q])

    useEffect(() => {
        if (debouncedSearch === lastWrittenQ.current) return
        lastWrittenQ.current = debouncedSearch
        history.replace({
            pathname: location.pathname,
            search: mergeRecordsSearch(location.search, {
                q: debouncedSearch,
            }),
        })
    }, [debouncedSearch])

    const unitsQuery = useRecordsUnits(pivot === 'units')
    const peopleSearching = debouncedSearch.length >= 2
    const peopleQuery = useRecordsPeople({
        q: peopleSearching ? debouncedSearch : undefined,
        cohort: peopleSearching ? undefined : cohort,
        page: peopleSearching ? undefined : page,
        enabled: pivot === 'people' || (pivot === 'units' && peopleSearching),
    })
    const sliceQuery = useRecordsServiceRequestSlice({
        category,
        openOnly,
        enabled:
            pivot === 'units' &&
            safeSelectedUnitId == null &&
            (Boolean(category) || openOnly),
    })

    const units = unitsQuery.data?.results ?? []
    const summary = unitsQuery.data?.summary
    const people = peopleQuery.data?.results ?? []
    const peopleCount = peopleQuery.data?.count ?? people.length
    const peoplePageCount = peopleQuery.data?.page_count ?? 1
    const peoplePageSize = peopleQuery.data?.page_size ?? 50
    const peopleTruncated = Boolean(peopleQuery.data?.truncated)

    useEffect(() => {
        if (peopleSearching || pivot !== 'people') return
        const actual = peopleQuery.data?.page
        if (actual == null || peopleQuery.isFetching) return
        if (actual === page) return
        history.replace({
            pathname: location.pathname,
            search: mergeRecordsSearch(location.search, { page: actual }),
        })
    }, [
        peopleQuery.data?.page,
        peopleQuery.isFetching,
        peopleSearching,
        pivot,
        page,
    ])

    const filteredUnits = useMemo(() => {
        const query = searchQuery.trim().toLowerCase()
        if (!query || pivot !== 'units') return units
        return units.filter(
            (unit) =>
                unit.name.toLowerCase().includes(query) ||
                unit.folder_path.toLowerCase().includes(query),
        )
    }, [units, searchQuery, pivot])

    const setPivot = (next: RecordsPivot) => {
        if (next === pivot) return
        history.push({
            pathname:
                next === 'people'
                    ? Routes.apartment.records.people
                    : Routes.apartment.records.root,
            search: buildRecordsSearch({
                q: searchQuery.trim(),
                cohort: next === 'people' ? cohort : null,
            }),
        })
    }

    const goPerson = (person: {
        user_id?: number | null
        lead_id?: number | null
    }) => {
        history.push({
            pathname: recordsPersonHref(person),
            search: buildRecordsSearch({
                q: searchQuery.trim(),
                cohort,
                page: peopleSearching ? 1 : page,
            }),
        })
    }

    const goAllUnits = (opts?: {
        category?: string | null
        open?: boolean
        occupancy?: RecordsSearchState['occupancy']
    }) => {
        history.push({
            pathname: Routes.apartment.records.root,
            search: buildRecordsSearch({
                q: searchQuery.trim(),
                category: opts?.category ?? null,
                open: Boolean(opts?.open),
                occupancy: opts?.occupancy ?? null,
            }),
        })
    }

    const openFirstMatch = () => {
        const qSearch = buildRecordsSearch({ q: searchQuery.trim() })
        if (pivot === 'people') {
            if (people.length === 1) {
                goPerson(people[0])
            }
            return
        }
        if (filteredUnits.length === 1 && people.length === 0) {
            history.push({
                pathname: recordsUnitPath(filteredUnits[0].id),
                search: qSearch,
            })
            return
        }
        if (filteredUnits.length === 0 && people.length === 1) {
            goPerson(people[0])
        }
    }

    return (
        <Container className={classes.root} direction="column">
            <Container
                className={classes.header}
                alignItems="center"
                justifyContent="flex-start"
            >
                <Container alignItems="center" style={{ flex: 1, gap: 16 }}>
                    <div className={classes.titleIcon}>
                        <FolderOpenIcon
                            style={{
                                color: theme.palette.common.white,
                                fontSize: 24,
                            }}
                        />
                    </div>
                    <div>
                        <div className={classes.title}>Records</div>
                        <div className={classes.subtitle}>
                            Look up a unit or a person
                        </div>
                    </div>
                    <Chip
                        icon={<ApartmentIcon style={{ fontSize: 16 }} />}
                        label="Units"
                        size="small"
                        clickable
                        color={pivot === 'units' ? 'primary' : 'default'}
                        onClick={() => setPivot('units')}
                        style={{ fontWeight: 600, marginLeft: 16 }}
                    />
                    <Chip
                        icon={<PeopleIcon style={{ fontSize: 16 }} />}
                        label="People"
                        size="small"
                        clickable
                        color={pivot === 'people' ? 'primary' : 'default'}
                        onClick={() => setPivot('people')}
                        style={{ fontWeight: 600 }}
                    />
                </Container>
                <TextField
                    className={classes.searchField}
                    placeholder={
                        pivot === 'units' ? 'Search units' : 'Search people'
                    }
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            openFirstMatch()
                        }
                    }}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    style={{
                                        color: theme.palette.text.secondary,
                                        fontSize: 20,
                                    }}
                                />
                            </InputAdornment>
                        ),
                    }}
                />
            </Container>

            <Container className={classes.mainContent}>
                <RecordsIndex
                    pivot={pivot}
                    units={filteredUnits}
                    unitsLoading={unitsQuery.isLoading}
                    people={people}
                    peopleLoading={peopleQuery.isLoading}
                    searchingPeople={peopleSearching}
                    suggestedPeople={
                        pivot === 'units' && peopleSearching ? people : []
                    }
                    suggestedPeopleLoading={
                        pivot === 'units' &&
                        peopleSearching &&
                        peopleQuery.isLoading
                    }
                    selectedUnitId={safeSelectedUnitId}
                    selectedUserId={safeSelectedUserId}
                    selectedLeadId={safeSelectedLeadId}
                    cohort={cohort}
                    onSelectCohort={(next: RecordsPeopleCohort) =>
                        history.push({
                            pathname: Routes.apartment.records.people,
                            search: buildRecordsSearch({
                                q: searchQuery.trim(),
                                cohort: next,
                            }),
                        })
                    }
                    onSelectPage={(nextPage) =>
                        history.push({
                            pathname: location.pathname,
                            search: buildRecordsSearch({
                                q: searchQuery.trim(),
                                cohort,
                                page: nextPage,
                            }),
                        })
                    }
                    peopleCount={peopleCount}
                    peoplePage={page}
                    peoplePageCount={peoplePageCount}
                    peoplePageSize={peoplePageSize}
                    peopleTruncated={peopleTruncated}
                    onSelectAll={() =>
                        history.push({
                            pathname:
                                pivot === 'people'
                                    ? Routes.apartment.records.people
                                    : Routes.apartment.records.root,
                            search: buildRecordsSearch({
                                q: searchQuery.trim(),
                                cohort: pivot === 'people' ? cohort : null,
                                page:
                                    pivot === 'people' && !peopleSearching
                                        ? page
                                        : 1,
                            }),
                        })
                    }
                    onSelectUnit={(unitId) =>
                        history.push({
                            pathname: recordsUnitPath(unitId),
                            search: buildRecordsSearch({
                                q: searchQuery.trim(),
                            }),
                        })
                    }
                    onSelectPerson={goPerson}
                />
                <Container className={classes.contentArea}>
                    {pivot === 'people' ? (
                        safeSelectedUserId != null ||
                        safeSelectedLeadId != null ? (
                            <PersonFile
                                userId={safeSelectedUserId}
                                leadId={safeSelectedLeadId}
                            />
                        ) : (
                            <div className={classes.pane}>
                                <div className={classes.emptyIcon}>
                                    <PeopleIcon
                                        style={{
                                            fontSize: 36,
                                            color: theme.palette.text.disabled,
                                        }}
                                    />
                                </div>
                                <div className={classes.emptyTitle}>
                                    All people
                                </div>
                                <div className={classes.emptyBody}>
                                    Anyone associated with this property —
                                    leads, applicants, current, future, past,
                                    and cancelled. Pick a cohort or search a
                                    name, email, or phone.
                                </div>
                            </div>
                        )
                    ) : safeSelectedUnitId != null ? (
                        <UnitFile unitId={safeSelectedUnitId} />
                    ) : (
                        <AllUnitsPane
                            summary={summary}
                            units={filteredUnits}
                            categories={sliceQuery.data?.categories ?? []}
                            requests={sliceQuery.data?.requests ?? []}
                            loading={
                                unitsQuery.isLoading ||
                                (sliceQuery.isFetching &&
                                    (Boolean(category) || openOnly))
                            }
                            category={category}
                            openOnly={openOnly}
                            occupancy={occupancy}
                            onSelectCategory={(key) =>
                                goAllUnits({
                                    category: key,
                                    open: false,
                                })
                            }
                            onSelectOpen={(open) =>
                                goAllUnits({ category: null, open })
                            }
                            onSelectOccupancy={(next) =>
                                goAllUnits({ occupancy: next })
                            }
                            onSelectUnit={(unitId) =>
                                history.push({
                                    pathname: recordsUnitPath(unitId),
                                    search: buildRecordsSearch({
                                        q: searchQuery.trim(),
                                    }),
                                })
                            }
                        />
                    )}
                </Container>
            </Container>
        </Container>
    )
}
